import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDocumentContext } from '../contexts/DocumentContext';
import { useManagerContext } from '../contexts/ManagerContext';
import { useEnhancedAuth } from '../contexts/EnhancedAuthContext';
import { Document, DocumentType } from '../types/Document';
import { buildPeriods, DocumentPeriod, isDocumentExpired } from '../utils/documentUtils';
import { useTemplates } from './useTemplates';

const REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const wasDocReminderSentToday = (typeId: string, userId: string): boolean => {
  try {
    const stored = localStorage.getItem(`doc_reminder_${typeId}_${userId}`);
    if (!stored) return false;
    return Date.now() - parseInt(stored, 10) < REMINDER_COOLDOWN_MS;
  } catch { return false; }
};
const markDocReminderSent = (typeId: string, userId: string): void => {
  try { localStorage.setItem(`doc_reminder_${typeId}_${userId}`, Date.now().toString()); } catch {}
};

interface UseDocumentTypeItemProps {
  type: DocumentType;
  isCompanyMode?: boolean;
}

interface DocumentTypeContextData {
  userDocuments: any[];
  loading: boolean;
  error: string | null;
  documentUpload: (formData: FormData) => Promise<any>;
  documentRemove: (docId: string) => Promise<any>;
  selectedUser: any;
  documentDownload: (url: string) => Promise<void>;
  sendUserDocumentReminders: (typeId: string, userId: string, managerId: string, message: string) => Promise<any>;
  setExpandedCategoryId: (id: string | null) => void;
  setExpandedSubcategoryId: (id: string | null) => void;
  highlightedDocumentId: string | null;
  setHighlightedDocumentId: (id: string | null) => void;
  userReminders: any;
  removeUserDocumentReminders: (typeId: string, userId: string) => Promise<any>;
  fetchUserReminders: (userId: string) => Promise<any>;
}

/**
 * Custom hook to manage document type item state and logic.
 * Supports both single-document-per-period (multi=false) and
 * multi-document-per-period (multi=true) types via the DocumentPeriod model.
 */
export const useDocumentTypeItem = ({ type, isCompanyMode = false }: UseDocumentTypeItemProps) => {
  const documentContext = useDocumentContext();
  const managerContext = useManagerContext();
  const { hasPermission, userDetails } = useEnhancedAuth();
  const { downloadTemplateByDocID } = useTemplates();

  // selectedPeriodKey tracks which period the user is viewing in the selector
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [hasReminder, setHasReminder] = useState(false);
  const [reminderJustSent, setReminderJustSent] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const template = type.template || null;

  // Context selection based on mode
  const contextData: DocumentTypeContextData = useMemo(() => isCompanyMode
    ? {
        userDocuments: managerContext?.companyDocuments || [],
        loading: managerContext?.loading || false,
        error: managerContext?.error || null,
        documentUpload: managerContext?.companyDocumentUpload || (() => Promise.resolve({ success: false, error: 'Company document upload not available', newDocument: null })),
        documentRemove: managerContext?.companyDocumentRemove || (() => Promise.resolve({ success: false, error: 'Company document removal not available' })),
        selectedUser: null,
        documentDownload: () => Promise.resolve(),
        sendUserDocumentReminders: () => Promise.resolve({}),
        setExpandedCategoryId: () => {},
        setExpandedSubcategoryId: () => {},
        highlightedDocumentId: null,
        setHighlightedDocumentId: () => {},
        userReminders: [],
        removeUserDocumentReminders: () => Promise.resolve({}),
        fetchUserReminders: () => Promise.resolve([]),
      }
    : {
        userDocuments: documentContext.userDocuments,
        loading: documentContext.loading,
        error: documentContext.error,
        documentUpload: documentContext.documentUpload,
        documentRemove: documentContext.documentRemove,
        selectedUser: documentContext.selectedUser,
        documentDownload: documentContext.documentDownload,
        sendUserDocumentReminders: documentContext.sendUserDocumentReminders,
        setExpandedCategoryId: documentContext.setExpandedCategoryId,
        setExpandedSubcategoryId: documentContext.setExpandedSubcategoryId,
        highlightedDocumentId: documentContext.highlightedDocumentId,
        setHighlightedDocumentId: documentContext.setHighlightedDocumentId,
        userReminders: documentContext.userReminders || [],
        removeUserDocumentReminders: documentContext.removeUserDocumentReminders,
        fetchUserReminders: documentContext.fetchUserReminders,
      }, [
        isCompanyMode,
        managerContext?.companyDocuments,
        managerContext?.loading,
        managerContext?.error,
        managerContext?.companyDocumentUpload,
        managerContext?.companyDocumentRemove,
        documentContext.userDocuments,
        documentContext.loading,
        documentContext.error,
        documentContext.documentUpload,
        documentContext.documentRemove,
        documentContext.selectedUser,
        documentContext.documentDownload,
        documentContext.sendUserDocumentReminders,
        documentContext.setExpandedCategoryId,
        documentContext.setExpandedSubcategoryId,
        documentContext.highlightedDocumentId,
        documentContext.setHighlightedDocumentId,
        documentContext.userReminders,
        documentContext.removeUserDocumentReminders,
        documentContext.fetchUserReminders,
      ]);

  // Build the canonical period list
  const periods: DocumentPeriod[] = useMemo(
    () => buildPeriods(
      (contextData.userDocuments as Document[]).filter(doc => (doc as any).typeID === type.id),
      type
    ),
    [contextData.userDocuments, type.id, type.recurrence, type.multi_uploads]
  );

  // currentPeriod is always periods[0] (built as the current slot by buildPeriods)
  const currentPeriod = periods[0] ?? null;

  // Initialise / reset selectedPeriodKey when the user or documents change
  useEffect(() => {
    setSelectedPeriodKey(currentPeriod?.key ?? '');
  }, [contextData.selectedUser?.id, currentPeriod?.key]);

  const selectedPeriod: DocumentPeriod | null =
    periods.find(p => p.key === selectedPeriodKey) ?? currentPeriod;

  // Check for reminders
  useEffect(() => {
    if (Array.isArray(contextData.userReminders?.documentReminders) && type.id) {
      const reminderExists = contextData.userReminders.documentReminders.some(
        (reminder: any) => reminder.document_type_id === type.id
      );
      setHasReminder(reminderExists);
    } else {
      setHasReminder(false);
    }
  }, [contextData.userReminders?.documentReminders, type.id]);

  // Reset reminderJustSent when selected user changes
  useEffect(() => {
    setReminderJustSent(false);
  }, [contextData.selectedUser?.id]);

  // Handle highlighting
  useEffect(() => {
    if (contextData.highlightedDocumentId === type.id) {
      setIsHighlighted(true);
    }
  }, [contextData.highlightedDocumentId, type.id]);

  useEffect(() => {
    let fadeTimeout: ReturnType<typeof setTimeout>;
    let removeTimeout: ReturnType<typeof setTimeout>;

    if (isHighlighted && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fadeTimeout = setTimeout(() => {
        setIsFading(true);
        removeTimeout = setTimeout(() => {
          setIsHighlighted(false);
          setIsFading(false);
          contextData.setHighlightedDocumentId(null);
          contextData.setExpandedCategoryId(null);
          contextData.setExpandedSubcategoryId(null);
        }, 1000);
      }, 2000);
    }

    return () => {
      clearTimeout(fadeTimeout);
      clearTimeout(removeTimeout);
    };
  }, [isHighlighted, contextData]);

  // Handlers
  const handlePeriodChange = useCallback((key: string) => {
    setSelectedPeriodKey(key);
  }, []);

  const handleUploadFormData = useCallback(async (formData: FormData) => {
    setProcessing(true);
    const result = await contextData.documentUpload(formData);

    if (result.success && result.error == null) {
      setShowUploadForm(false);
      // After upload navigate to the current period to show the new doc
      setSelectedPeriodKey(currentPeriod?.key ?? '');

      if (hasReminder && contextData.selectedUser) {
        await contextData.removeUserDocumentReminders(type.id, contextData.selectedUser.id);
        if (contextData.selectedUser?.id && typeof contextData.fetchUserReminders === 'function') {
          await contextData.fetchUserReminders(contextData.selectedUser.id);
        }
      }
    } else {
      console.error('Upload failed:', result.error);
    }

    setProcessing(false);
    return result;
  }, [contextData, hasReminder, type.id, currentPeriod?.key]);

  const handleRemoveDocument = useCallback(async (doc: Document) => {
    setProcessing(true);
    const result = await contextData.documentRemove(doc.id);
    if (!result.success) {
      console.error('Delete failed:', result.error);
    }
    setProcessing(false);
    return result;
  }, [contextData]);

  const handleSendReminder = useCallback(async (typeID: string) => {
    if (!contextData.selectedUser || !userDetails) return;
    if (wasDocReminderSentToday(typeID, contextData.selectedUser.id)) return;
    setProcessing(true);
    setReminderJustSent(true);
    try {
      await contextData.sendUserDocumentReminders(
        typeID,
        contextData.selectedUser.id,
        userDetails.id,
        `Please upload your ${type.name}`
      );
      if (contextData.selectedUser?.id && typeof contextData.fetchUserReminders === 'function') {
        await contextData.fetchUserReminders(contextData.selectedUser.id);
      }
      markDocReminderSent(typeID, contextData.selectedUser.id);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      setReminderJustSent(false);
    } finally {
      setProcessing(false);
    }
  }, [contextData, userDetails, type.name]);

  const handleTemplateDownload = useCallback(async (docTypeId: string, selectedUserId: string | undefined) => {
    if (docTypeId && selectedUserId) {
      const fileName = `${template.doc_name}.${template.fileType.toLowerCase()}`;
      await downloadTemplateByDocID(docTypeId, selectedUserId, { suggestedFilename: fileName, fileType: template.fileType });
    } else {
      console.error('Document type ID or selected user ID is missing for template download');
    }
  }, [downloadTemplateByDocID, template]);

  // Permission checks
  const canEdit = useMemo(() => {
    if (isCompanyMode) {
      // Use native column names: edit_role_id and view_role_id
      const roles = managerContext?.roles || [];
      const roleById = new Map(roles.map((r: any) => [r.id, r]));
      
      if (type.edit_role_id && userDetails?.accessLevel !== undefined) {
        const editRole = roleById.get(type.edit_role_id);
        if (editRole && editRole.access_level !== undefined) {
          return userDetails.accessLevel >= editRole.access_level;
        }
      }
      return false;
    }
    return !!(userDetails && contextData.selectedUser &&
      userDetails.id === contextData.selectedUser.id &&
      !contextData.selectedUser.archived);
  }, [isCompanyMode, type.edit_role_id, managerContext?.roles, userDetails?.accessLevel, userDetails?.id, contextData.selectedUser?.id, contextData.selectedUser?.archived]);

  const canManageUsers = hasPermission('user_manage_agency');
  const isViewingOwnDocuments = !!(userDetails && contextData.selectedUser && userDetails.id === contextData.selectedUser.id);
  const isViewingOtherUser = !!(canManageUsers && contextData.selectedUser && userDetails && userDetails.id !== contextData.selectedUser.id);

  // For reminder/missing logic use the current period's document status
  const currentPeriodDoc = currentPeriod?.documents[0] ?? null;
  const isMissing = !currentPeriodDoc;
  const isExpired = currentPeriodDoc && isDocumentExpired(currentPeriodDoc);

  const reminderSentToday = useMemo(() => {
    if (!contextData.selectedUser?.id || !type.id) return false;
    return wasDocReminderSentToday(type.id, contextData.selectedUser.id);
  }, [contextData.selectedUser?.id, type.id, reminderJustSent]);

  const showReminderIndicator = isViewingOwnDocuments && hasReminder;
  const showSendReminderButton = isViewingOtherUser && (isMissing || isExpired) && !hasReminder;
  const showReminderSentIndicator = isViewingOtherUser && hasReminder;

  // Upload button visible when current period is selected and (multi OR no doc yet)
  const isCurrentPeriodSelected = selectedPeriod?.isCurrent ?? true;
  const showUploadButton = canEdit && isCurrentPeriodSelected && !showUploadForm &&
    (type.multi_uploads || (selectedPeriod?.documents.length ?? 0) === 0);

  return {
    // State
    selectedPeriodKey,
    showUploadForm,
    processing,
    isHighlighted,
    isFading,
    hasReminder,
    reminderJustSent,
    reminderSentToday,
    containerRef,

    // Period data
    periods,
    currentPeriod,
    selectedPeriod,
    template,

    // Context data
    loading: contextData.loading,
    error: contextData.error,
    documentDownload: contextData.documentDownload,

    // Handlers
    handleUploadFormData,
    handleRemoveDocument,
    handlePeriodChange,
    handleSendReminder,
    handleTemplateDownload,
    setShowUploadForm,
    refetch: managerContext?.refetch || (() => {}),

    // Permission/UI flags
    canEdit,
    showReminderIndicator,
    showSendReminderButton,
    showReminderSentIndicator,
    showUploadButton,
    isCompanyMode,
  };
};
